import React from 'react';
import { bool } from 'prop-types';
import { text } from 'polyglot/polyglot';
import clsx from 'clsx';
import { formatCurrency } from 'utils/currency';

import useCurrentGameStore from 'stores/current-game';
import useUpcomingGameStore from 'stores/upcoming-game';
import usePrizeTypesStore from 'stores/prize-types';
import useGameStore, { PURCHASE_VIEW, ACTIVE_GAME } from 'stores/game';

import RocketIcon from 'themes/common/svg/rocket-icon.svg';

import BingoSquare from './BingoSquare/BingoSquare';

import styles from './ListOfPrizes.module.scss';

const currentGameSelector = (state) => state.currentGame;
const nextPrizeLevelSelector = (state) => state.currentGame.nextPrizeLevel;
const nextGameSelector = (state) => state.nextGame;
const bingoRoundIdSelector = (state) => state.currentGame.bingoRoundId;
const prizeTypesSelector = (state) => state.prizes;
const gameStateSelector = (state) => state.gameState;

function ListOfPrizes({
  isExpandedView,
  purchaseView,
  isVertical,
  isReplay,
  displayNextGame,
}) {
  const currentGame = useCurrentGameStore(currentGameSelector);
  const nextGame = useUpcomingGameStore(nextGameSelector);
  const activeGame = displayNextGame ? nextGame : currentGame;
  const prizes = displayNextGame ? nextGame.prizes : currentGame.prizes;
  const activePrizeLevel = useCurrentGameStore(nextPrizeLevelSelector);
  const bingoRoundId = useCurrentGameStore(bingoRoundIdSelector);
  const prizeTypes = usePrizeTypesStore(prizeTypesSelector);
  const gameState = useGameStore(gameStateSelector);
  const potBoosterAnimationDone = useGameStore(
    (state) => state.potBoosterAnimationDone
  );
  const activePrize =
    purchaseView || isReplay || displayNextGame ? null : activePrizeLevel;

  const isPurchase = gameState === PURCHASE_VIEW;
  const isActiveGame = gameState === ACTIVE_GAME;

  return (
    <div key={bingoRoundId} className={styles.wrapper}>
      {prizes &&
        prizes.map((item) => (
          <div
            key={`prize-${item.prizeTypeCode}-${item.aggregateAmount}`}
            className={clsx(styles.prizeContainer, {
              [styles.threeRows]: item.prizeTypeCode === '1rowverthordiag',
              [styles.fourRows]: item.prizeTypeCode === '1rowverthordiag4cor',
              [styles.dark]: activePrize && activePrize === item.orderNumber,
              [styles.isExpandedView]: isExpandedView,
              [styles.isVertical]: isVertical,
              [styles.isReplay]: isReplay,
              [styles.isActive]: activePrize === item.orderNumber,
              [styles.isPotBoosterPurchaseView]:
                currentGame.potBoosterActive && isPurchase,
              [styles.isPotBoosterActiveGameView]:
                potBoosterAnimationDone &&
                currentGame.potBoosterActive &&
                (isActiveGame || isReplay) &&
                currentGame.selectedPotBoosterMultiplier > 1,
            })}
          >
            <span className={styles.background}>
              <div className={styles.flexRow}>
                <span
                  className={clsx(styles.text, {
                    [styles.short]:
                      item.prizeTypeCode.includes('1rowverthordiag') ||
                      item.prizeTypeCode === '1rowhor' ||
                      item.prizeTypeCode === '1rowvert' ||
                      item.prizeTypeCode === '1rowdiag',
                  })}
                >
                  <span
                    className={styles.textFade}
                    title={
                      activeGame.prizeTypeCode
                        ? text.t(
                            `prizeplan.bingoPatterns.${activeGame.prizeTypeCode}`
                          )
                        : text.t(
                            `prizeplan.bingoPatterns.${item.prizeTypeCode}`
                          )
                    }
                  >
                    {activeGame.prizeTypeCode
                      ? text.t(
                          `prizeplan.bingoPatterns.${activeGame.prizeTypeCode}`
                        )
                      : text.t(`prizeplan.bingoPatterns.${item.prizeTypeCode}`)}
                  </span>
                </span>
                {item.prizeTypeCode.includes('1rowverthordiag') ? (
                  <span
                    className={clsx(styles.iconList, {
                      [styles.iconListThree]:
                        item.prizeTypeCode === '1rowverthordiag',
                      [styles.iconListFour]:
                        item.prizeTypeCode === '1rowverthordiag4cor',
                    })}
                  >
                    <BingoSquare
                      type="1rowhor"
                      layout={activeGame.gameTypeCode}
                      isDark={
                        activePrize === item.orderNumber ||
                        (currentGame.potBoosterActive &&
                          (isActiveGame || isReplay) &&
                          currentGame.selectedPotBoosterMultiplier > 1)
                      }
                      isVertical={isVertical}
                      isGolden={
                        potBoosterAnimationDone &&
                        activePrize === item.orderNumber &&
                        currentGame.potBoosterActive &&
                        currentGame.selectedPotBoosterMultiplier > 1
                      }
                      isPotBoosterActive={
                        currentGame.potBoosterActive &&
                        currentGame.selectedPotBoosterMultiplier > 1
                      }
                    />
                    {!isVertical && (
                      <BingoSquare
                        type="1rowvert"
                        layout={activeGame.gameTypeCode}
                        isDark={
                          activePrize === item.orderNumber ||
                          (currentGame.potBoosterActive &&
                            (isActiveGame || isReplay) &&
                            currentGame.selectedPotBoosterMultiplier > 1)
                        }
                        isGolden={
                          potBoosterAnimationDone &&
                          activePrize === item.orderNumber &&
                          currentGame.potBoosterActive &&
                          currentGame.selectedPotBoosterMultiplier > 1
                        }
                        isPotBoosterActive={
                          currentGame.potBoosterActive &&
                          currentGame.selectedPotBoosterMultiplier > 1
                        }
                      />
                    )}
                    {!isVertical && (
                      <BingoSquare
                        type="1rowdiag"
                        layout={activeGame.gameTypeCode}
                        isDark={
                          activePrize === item.orderNumber ||
                          (currentGame.potBoosterActive &&
                            (isActiveGame || isReplay) &&
                            currentGame.selectedPotBoosterMultiplier > 1)
                        }
                        isGolden={
                          potBoosterAnimationDone &&
                          activePrize === item.orderNumber &&
                          currentGame.potBoosterActive &&
                          currentGame.selectedPotBoosterMultiplier > 1
                        }
                        isPotBoosterActive={
                          currentGame.potBoosterActive &&
                          currentGame.selectedPotBoosterMultiplier > 1
                        }
                      />
                    )}
                    {!isVertical &&
                      item.prizeTypeCode === '1rowverthordiag4cor' && (
                        <BingoSquare
                          type="4cor"
                          layout={activeGame.gameTypeCode}
                          isDark={
                            activePrize === item.orderNumber ||
                            (currentGame.potBoosterActive &&
                              (isActiveGame || isReplay) &&
                              currentGame.selectedPotBoosterMultiplier > 1)
                          }
                          isGolden={
                            potBoosterAnimationDone &&
                            activePrize === item.orderNumber &&
                            currentGame.potBoosterActive &&
                            currentGame.selectedPotBoosterMultiplier > 1
                          }
                          isPotBoosterActive={
                            currentGame.potBoosterActive &&
                            currentGame.selectedPotBoosterMultiplier > 1
                          }
                        />
                      )}
                  </span>
                ) : (
                  <span className={styles.icon}>
                    <BingoSquare
                      layout={activeGame.gameTypeCode}
                      type={
                        prizeTypes[activeGame.gameTypeName]
                          ? prizeTypes[activeGame.gameTypeName].pattern
                          : item.prizeTypeCode
                      }
                      isDark={
                        activePrize === item.orderNumber ||
                        (currentGame.potBoosterActive &&
                          (isActiveGame || isReplay) &&
                          currentGame.selectedPotBoosterMultiplier > 1)
                      }
                      isVertical={isVertical}
                      isGolden={
                        potBoosterAnimationDone &&
                        activePrize === item.orderNumber &&
                        currentGame.potBoosterActive &&
                        currentGame.selectedPotBoosterMultiplier > 1
                      }
                      isPotBoosterActive={
                        currentGame.potBoosterActive &&
                        currentGame.selectedPotBoosterMultiplier > 1
                      }
                    />
                  </span>
                )}
                <span className={styles.prize}>
                  {formatCurrency(item.aggregateAmount)}
                </span>
                {currentGame.potBoosterActive && isPurchase && (
                  <RocketIcon className={styles.rocketIcon} />
                )}
              </div>
              {item.oneToGoPrizeInPercent > 0 && (
                <div className={styles.flexRow}>
                  <span className={clsx(styles.text, styles.togo)}>
                    - {text.t('prizeplan.toGo', { number: 1 })}
                  </span>
                  <span className={styles.prize}>
                    {formatCurrency(item.oneToGoPrizeAmount)}
                  </span>
                </div>
              )}

              {item.twoToGoPrizeInPercent > 0 && (
                <div className={styles.flexRow}>
                  <span className={clsx(styles.text, styles.togo)}>
                    - {text.t('prizeplan.toGo', { number: 2 })}
                  </span>
                  <span className={styles.prize}>
                    {formatCurrency(item.twoToGoPrizeAmount)}
                  </span>
                </div>
              )}
            </span>
          </div>
        ))}
    </div>
  );
}

ListOfPrizes.propTypes = {
  isExpandedView: bool,
  purchaseView: bool,
  isVertical: bool,
  isReplay: bool,
  displayNextGame: bool,
};

ListOfPrizes.defaultProps = {
  isExpandedView: false,
  purchaseView: false,
  isVertical: false,
  isReplay: false,
  displayNextGame: false,
};
export default ListOfPrizes;
